// CRM LinkedIn Batches — rehoused from classic LinkedinBatches.js.
// Two-column shell reusing .crmeb-* styles. Export is a zip of profiles.csv + variables.csv (LinkedHelper).
// Every classic endpoint and action preserved.
import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

function avatarColour(name) {
  const palette = ['#5B3FD6','#0B5BB5','#056F4E','#B25400','#066576','#A21C5A','#3F4956','#7A4D00','#2E8FFF','#7C5CFC','#00A878','#C8202E','#8B5CF6','#0EA5E9','#D97706','#DB2777','#059669','#4F46E5'];
  let h = 0; for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) % palette.length;
  return palette[h];
}
function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// LinkedIn glyph for the empty-state mark.
const LI_MARK = (
  <svg width="30" height="30" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="2.5" width="11" height="11" rx="2.2"/>
    <path d="M5 6.8v4"/><circle cx="5" cy="4.9" r="0.5"/>
    <path d="M7.5 11V6.8M7.5 8.4a1.6 1.6 0 0 1 3.2 0V11"/>
  </svg>
);

export default function CrmLinkedinBatches() {
  const { clientId } = useOutletContext();
  const { getToken } = useAuth();

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [batchContacts, setBatchContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const authFetch = useCallback(async (url, opts = {}) => {
    const token = await getToken();
    return fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
  }, [getToken]);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${clientId}/linkedin-batches`);
      if (!res.ok) throw new Error('Failed to load batches');
      setBatches(await res.json());
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [authFetch, clientId]);

  const loadBatchContacts = useCallback(async (batchId) => {
    setContactsLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/linkedin-batches/${batchId}/contacts`);
      setBatchContacts(res.ok ? await res.json() : []);
    } catch (e) { setBatchContacts([]); }
    finally { setContactsLoading(false); }
  }, [authFetch]);

  useEffect(() => { loadBatches(); }, [loadBatches]);
  useEffect(() => { if (selectedId) loadBatchContacts(selectedId); }, [selectedId, loadBatchContacts]);

  async function createBatch() {
    if (!newName.trim()) { toast.error('Batch name required'); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API_BASE}/clients/${clientId}/linkedin-batches`, {
        method: 'POST', body: JSON.stringify({ name: newName.trim() }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Failed to create batch'); }
      const created = await res.json().catch(() => null);
      setNewName(''); setShowCreate(false);
      toast.success('Batch created');
      await loadBatches();
      if (created && created.id) setSelectedId(created.id);
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  }

  async function renameBatch(batchId, currentName) {
    const next = window.prompt('Rename batch', currentName);
    if (!next || !next.trim() || next.trim() === currentName) return;
    try {
      const res = await authFetch(`${API_BASE}/linkedin-batches/${batchId}`, { method: 'PUT', body: JSON.stringify({ name: next.trim() }) });
      if (!res.ok) throw new Error('Rename failed');
      toast.success('Renamed'); await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  async function deleteBatch(batchId, name) {
    if (!window.confirm(`Delete batch "${name}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API_BASE}/linkedin-batches/${batchId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Batch deleted');
      if (selectedId === batchId) setSelectedId(null);
      await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  async function exportBatch(batchId) {
    setExporting(batchId);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/linkedin-batches/${batchId}/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || err.error || 'Export failed'); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `linkedin-batch-${batchId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
      await loadBatches();
    } catch (e) { toast.error(e.message); }
    finally { setExporting(null); }
  }

  async function removeContact(batchId, contactId) {
    if (!window.confirm('Remove this contact from the batch?')) return;
    try {
      const res = await authFetch(`${API_BASE}/linkedin-batches/${batchId}/contacts/${contactId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Remove failed');
      toast.success('Contact removed');
      await loadBatchContacts(batchId); await loadBatches();
    } catch (e) { toast.error(e.message); }
  }

  const totalContacts = batches.reduce((s, b) => s + Number(b.contact_count || 0), 0);
  const readyCount = batches.filter(b => b.status !== 'exported' && Number(b.contact_count) > 0).length;
  const exportedCount = batches.filter(b => b.status === 'exported').length;

  const filtered = batches.filter(b => {
    if (statusFilter !== 'all' && (b.status || 'draft') !== statusFilter) return false;
    if (q.trim() && !(b.name || '').toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  });
  const selected = batches.find(b => b.id === selectedId) || null;

  return (
    <div className="crmeb">
      <div className="crmeb-head">
        <div>
          <h1 className="crmeb-title">LinkedIn Batches</h1>
          <p className="crmeb-sub">Group contacts with approved LinkedIn sequences and export a LinkedHelper-ready zip.</p>
        </div>
        <button className="crmeb-new" onClick={() => setShowCreate(v => !v)}>
          <span className="crmeb-new-plus">+</span> New batch
        </button>
      </div>

      <div className="crmeb-stats">
        <div className="crmeb-stat"><div className="crmeb-stat-n">{batches.length}</div><div className="crmeb-stat-l">Batches</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n">{totalContacts}</div><div className="crmeb-stat-l">Contacts</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n" style={{ color: '#B25400' }}>{readyCount}</div><div className="crmeb-stat-l">Ready to export</div></div>
        <div className="crmeb-stat"><div className="crmeb-stat-n" style={{ color: '#1A7A4C' }}>{exportedCount}</div><div className="crmeb-stat-l">Exported</div></div>
      </div>

      {showCreate && (
        <div className="crmeb-create">
          <input
            className="crmeb-create-name" type="text" autoFocus value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createBatch(); if (e.key === 'Escape') setShowCreate(false); }}
            placeholder="Batch name (eg. October UK accountants)"
          />
          <button className="crmeb-create-go" onClick={createBatch} disabled={creating}>{creating ? 'Creating…' : 'Create batch'}</button>
        </div>
      )}

      <div className="crmeb-body">
        <div className="crmeb-rail">
          <div className="crmeb-rail-tools">
            <input className="crmeb-search" placeholder="Search batches" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="crmeb-segbar">
              {['all', 'draft', 'exported'].map(s => (
                <button key={s} className={'crmeb-seg' + (statusFilter === s ? ' crmeb-seg--on' : '')} onClick={() => setStatusFilter(s)}>
                  {s === 'all' ? 'All' : s === 'draft' ? 'Draft' : 'Exported'}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="crmeb-muted">Loading batches…</div>
          ) : filtered.length === 0 ? (
            <div className="crmeb-rail-empty">{batches.length === 0 ? 'No batches yet. Create one to get started.' : 'No batches match.'}</div>
          ) : (
            <div className="crmeb-rail-list">
              {filtered.map(b => {
                const isExported = b.status === 'exported';
                return (
                  <button key={b.id} className={'crmeb-card' + (selectedId === b.id ? ' crmeb-card--on' : '')} onClick={() => setSelectedId(b.id)}>
                    <span className="crmeb-card-dot" style={{ background: isExported ? '#1A7A4C' : '#B25400' }} />
                    <span className="crmeb-card-main">
                      <span className="crmeb-card-name">{b.name}</span>
                      <span className="crmeb-card-meta">
                        <span className="crmeb-card-count">{b.contact_count} {Number(b.contact_count) === 1 ? 'contact' : 'contacts'}</span>
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="crmeb-detail">
          {!selected ? (
            <div className="crmeb-detail-empty">
              <div className="crmeb-detail-empty-mark">{LI_MARK}</div>
              <div className="crmeb-detail-empty-t">Select a batch</div>
              <div className="crmeb-detail-empty-s">Pick a batch on the left to view its contacts and export.</div>
            </div>
          ) : (
            <>
              <div className="crmeb-dhead">
                <div className="crmeb-dhead-l">
                  <div className="crmeb-dhead-top">
                    <h2 className="crmeb-dhead-name">{selected.name}</h2>
                    <span className={'crmeb-status ' + (selected.status === 'exported' ? 'crmeb-status--exp' : 'crmeb-status--draft')}>
                      {selected.status === 'exported' ? 'Exported' : 'Draft'}
                    </span>
                  </div>
                  <div className="crmeb-dhead-meta">
                    <span>{selected.contact_count} {Number(selected.contact_count) === 1 ? 'contact' : 'contacts'}</span>
                    <span>Created {formatDate(selected.created_at)}</span>
                  </div>
                </div>
                <div className="crmeb-dhead-actions">
                  <button className="crmeb-btn" onClick={() => renameBatch(selected.id, selected.name)}>Rename</button>
                  <button className="crmeb-btn crmeb-btn--danger" onClick={() => deleteBatch(selected.id, selected.name)}>Delete</button>
                  <button className="crmeb-btn crmeb-btn--primary" disabled={Number(selected.contact_count) === 0 || exporting === selected.id}
                    onClick={() => exportBatch(selected.id)}>
                    {exporting === selected.id ? 'Preparing…' : 'Download CSV'}
                  </button>
                </div>
              </div>

              <div className="crmeb-dbody">
                {contactsLoading ? (
                  <div className="crmeb-muted">Loading contacts…</div>
                ) : batchContacts.length === 0 ? (
                  <div className="crmeb-detail-empty">
                    <div className="crmeb-detail-empty-t">No contacts yet</div>
                    <div className="crmeb-detail-empty-s">Add contacts from Sequences using “Add to batch”, then export here.</div>
                  </div>
                ) : (
                  <div className="crmeb-contacts">
                    {batchContacts.map(bc => (
                      <div key={bc.id} className="crmeb-contact">
                        <span className="crmeb-avatar" style={{ background: avatarColour(bc.full_name) }}>{initials(bc.full_name)}</span>
                        <span className="crmeb-contact-main">
                          <span className="crmeb-contact-name">{bc.full_name || '—'}</span>
                          <span className="crmeb-contact-sub">
                            {bc.job_title ? <span className="crmeb-contact-co">{bc.job_title}</span> : null}
                            {bc.company_name ? <span className="crmeb-contact-co">{bc.company_name}</span> : null}
                            {bc.linkedin_url ? <a className="crmeb-contact-email" href={bc.linkedin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>LinkedIn</a> : null}
                          </span>
                        </span>
                        <button className="crmeb-remove" onClick={() => removeContact(selected.id, bc.contact_id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
